import { useContext, useEffect, useState } from 'react';
import ThemeContext from '../../contexts/ThemeContext';
import './HelpRouter.css';

const DESTINATIONS = [
    {
        id: 'book',
        href: 'https://book.qbmedia.co.il',
        accent: '#f47a1b',
        external: true,
    },
    {
        id: 'build',
        href: 'https://dev.qbmedia.co.il',
        accent: '#3498db',
        external: true,
    },
    {
        id: 'newClient',
        href: 'https://newclient.qbmedia.co.il',
        accent: '#2ecc71',
        external: true,
    },
    {
        id: 'menus',
        href: 'https://menus.qbmedia.co.il',
        accent: '#9b59b6',
        external: true,
    },
    {
        id: 'pricing',
        href: '#pricing',
        accent: '#e67e22',
        external: false,
    },
];

/**
 * Soft intent helper — opens only when asked (FAB).
 * Screen 1: mood · Screen 2: destination shortcuts.
 */
const HelpRouter = ({
    open,
    onClose,
    copy,
    font = 'ar',
    dir = 'rtl',
    txtAlign = 'right',
}) => {
    const { theme } = useContext(ThemeContext);
    const [step, setStep] = useState('mood'); // mood | routes

    useEffect(() => {
        if (!open) {
            setStep('mood');
            return;
        }
        const onKey = (e) => e.key === 'Escape' && onClose();
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = '';
            document.removeEventListener('keydown', onKey);
        };
    }, [open, onClose]);

    if (!open || !copy) return null;

    const handleBrowse = () => {
        try {
            sessionStorage.setItem('qb-help-browsing', '1');
            sessionStorage.setItem('qb-help-seen', '1');
        } catch (_) {
            /* ignore */
        }
        onClose();
    };

    const goRoutes = () => setStep('routes');

    return (
        <div
            className="HelpRouter-overlay"
            role="presentation"
            onClick={onClose}
        >
            <div
                className={`HelpRouter-modal HelpRouter-modal--${theme.mode} ${font}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="help-router-title"
                style={{
                    backgroundColor: theme.cardsBg,
                    color: theme.text,
                    direction: dir,
                    textAlign: txtAlign,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    className="HelpRouter-close"
                    onClick={onClose}
                    aria-label={copy.close}
                    style={{ color: theme.text }}
                >
                    ×
                </button>

                {step === 'mood' ? (
                    <>
                        <p className="HelpRouter-eyebrow">{copy.eyebrow}</p>
                        <h2 id="help-router-title" className="HelpRouter-title">
                            {copy.title}
                        </h2>
                        <p className="HelpRouter-subtitle">{copy.subtitle}</p>

                        <div className="HelpRouter-moods">
                            <button
                                type="button"
                                className="HelpRouter-mood HelpRouter-mood--primary"
                                onClick={goRoutes}
                            >
                                <span className="HelpRouter-mood-label">
                                    {copy.moods.help}
                                </span>
                                <span className="HelpRouter-mood-hint">
                                    {copy.moods.helpHint}
                                </span>
                            </button>
                            <button
                                type="button"
                                className="HelpRouter-mood HelpRouter-mood--ghost"
                                onClick={handleBrowse}
                            >
                                <span className="HelpRouter-mood-label">
                                    {copy.moods.browse}
                                </span>
                                <span className="HelpRouter-mood-hint">
                                    {copy.moods.browseHint}
                                </span>
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <button
                            type="button"
                            className="HelpRouter-back"
                            onClick={() => setStep('mood')}
                        >
                            {copy.back}
                        </button>
                        <h2 id="help-router-title" className="HelpRouter-title">
                            {copy.routesTitle}
                        </h2>
                        <p className="HelpRouter-subtitle">{copy.routesSubtitle}</p>

                        <div className="HelpRouter-routes">
                            {DESTINATIONS.map((dest) => {
                                const item = copy.routes?.[dest.id];
                                if (!item) return null;

                                if (!dest.external) {
                                    return (
                                        <button
                                            key={dest.id}
                                            type="button"
                                            className="HelpRouter-route"
                                            style={{ '--route-accent': dest.accent }}
                                            onClick={() => {
                                                onClose();
                                                const el = document.querySelector(dest.href);
                                                if (el) {
                                                    el.scrollIntoView({
                                                        behavior: 'smooth',
                                                        block: 'start',
                                                    });
                                                }
                                            }}
                                        >
                                            <span
                                                className="HelpRouter-route-mark"
                                                aria-hidden="true"
                                            />
                                            <span className="HelpRouter-route-body">
                                                <span className="HelpRouter-route-title">
                                                    {item.title}
                                                </span>
                                                <span className="HelpRouter-route-desc">
                                                    {item.desc}
                                                </span>
                                            </span>
                                            <span
                                                className="HelpRouter-route-arrow"
                                                aria-hidden="true"
                                            >
                                                {dir === 'rtl' ? '←' : '→'}
                                            </span>
                                        </button>
                                    );
                                }

                                return (
                                    <a
                                        key={dest.id}
                                        className="HelpRouter-route"
                                        href={dest.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ '--route-accent': dest.accent }}
                                        onClick={onClose}
                                    >
                                        <span
                                            className="HelpRouter-route-mark"
                                            aria-hidden="true"
                                        />
                                        <span className="HelpRouter-route-body">
                                            <span className="HelpRouter-route-title">
                                                {item.title}
                                            </span>
                                            <span className="HelpRouter-route-desc">
                                                {item.desc}
                                            </span>
                                        </span>
                                        <span
                                            className="HelpRouter-route-arrow"
                                            aria-hidden="true"
                                        >
                                            {dir === 'rtl' ? '←' : '→'}
                                        </span>
                                    </a>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default HelpRouter;
